import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  PhoneCall, PlayCircle, CheckCircle2, SkipForward, RotateCcw, Loader2,
  Stethoscope, Pill, User, ChevronRight, ListOrdered, Timer,
} from "lucide-react";
import { toast } from "sonner";
import { queueAction, callNextPatient } from "../../lib/queueApi";
import { TokenBadge, PriorityFlag, StatusBadge, fmtTime, waitedMinutes } from "./QueueBits";
import PrescriptionDialog from "../PrescriptionDialog";

/**
 * Doctor view — drives the consultation: CALL NEXT → START → COMPLETE,
 * with SKIP and RECALL for patients who are not at the door.
 * Completing a consultation never settles a bill; that stays with reception.
 */
export default function DoctorQueueView({ board, doctorId, doctorName, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [rxFor, setRxFor] = useState(null);

  const current = board?.in_consultation?.[0] || null;
  const called = board?.called?.[0] || null;
  const waiting = board?.waiting || [];
  const skipped = board?.skipped || [];
  const completed = board?.completed || [];
  const active = current || called;

  const run = async (label, fn) => {
    setBusy(label);
    const res = await fn();
    setBusy("");
    if (res?.ok) onChanged && onChanged();
    return res;
  };

  const onCallNext = () =>
    run("call-next", async () => {
      const res = await callNextPatient(doctorId);
      if (res.ok) {
        if (res.data.called) toast.success(`Called ${res.data.called.patient_name}`);
        else toast.message("No one is waiting");
      }
      return res;
    });

  return (
    <div className="space-y-6" data-testid="doctor-queue-view">
      {/* Active patient */}
      <Card
        className={`border-2 ${
          current
            ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-white"
            : called
            ? "border-blue-500 bg-gradient-to-br from-blue-50 to-white"
            : "border-dashed border-slate-300 bg-slate-50/40"
        }`}
        data-testid="doctor-current-card"
      >
        <CardContent className="p-6 lg:p-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
            {current ? <Stethoscope className="w-4 h-4 text-emerald-600" /> : <PhoneCall className="w-4 h-4 text-blue-600" />}
            {current ? "In consultation" : called ? "Called — waiting at the door" : "No active patient"}
          </div>

          {active ? (
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="min-w-0">
                <div
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2 truncate"
                  style={{ fontFamily: "Manrope" }}
                  data-testid="doctor-current-name"
                >
                  {active.patient_name}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <TokenBadge entry={active} />
                  {active.priority && <PriorityFlag />}
                  <StatusBadge status={active.queue_status} />
                  <span className="text-sm text-slate-600">{active.reason || "Consultation"}</span>
                  {active.consultation_started_at && (
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Timer className="w-3 h-3" /> started {fmtTime(active.consultation_started_at)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/patients/${active.patient_id}`)}
                  data-testid="doctor-history-btn"
                >
                  <User className="w-4 h-4 mr-2" /> History
                </Button>
                {current && (
                  <Button variant="outline" onClick={() => setRxFor(current)} data-testid="doctor-prescribe-btn">
                    <Pill className="w-4 h-4 mr-2 text-violet-600" /> Prescribe
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => run("skip", () => queueAction(active.appointment_id, "skip"))}
                  disabled={!!busy || active.queue_status === "in_consultation"}
                  data-testid="doctor-skip-btn"
                >
                  <SkipForward className="w-4 h-4 mr-2 text-amber-600" /> Skip
                </Button>
                {called && !current ? (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => run("start", () => queueAction(called.appointment_id, "start"))}
                    disabled={!!busy}
                    data-testid="doctor-start-btn"
                  >
                    {busy === "start" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                    Start consultation
                  </Button>
                ) : (
                  <Button
                    className="bg-slate-900 hover:bg-slate-800"
                    onClick={() => run("complete", async () => {
                      const res = await queueAction(current.appointment_id, "complete");
                      if (res.ok) toast.success("Consultation completed · sent to billing");
                      return res;
                    })}
                    disabled={!!busy}
                    data-testid="doctor-complete-btn"
                  >
                    {busy === "complete" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Complete consultation
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="text-slate-400 italic" data-testid="doctor-no-active">
                Nobody is with you right now. Call the next patient to begin.
              </div>
              <Button
                size="lg"
                onClick={onCallNext}
                disabled={!!busy || waiting.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="doctor-call-next-btn"
              >
                {busy === "call-next" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                Call next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Next patients */}
        <Card className="border-slate-200" data-testid="doctor-next-list">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <ListOrdered className="w-4 h-4 text-blue-600" /> Next patients ({waiting.length})
              </h3>
              {active && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCallNext}
                  disabled={!!busy || waiting.length === 0 || !!called}
                  data-testid="doctor-call-next-inline-btn"
                  title={called ? "Start or skip the called patient first" : "Call the next patient"}
                >
                  <PhoneCall className="w-3.5 h-3.5 mr-1.5" /> Call next
                </Button>
              )}
            </div>

            {waiting.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400 italic" data-testid="doctor-queue-empty">
                Queue is empty
              </div>
            ) : (
              <div className="space-y-2">
                {waiting.map((e, idx) => (
                  <div
                    key={e.appointment_id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                    data-testid={`doctor-waiting-row-${e.appointment_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{e.patient_name}</div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <TokenBadge entry={e} />
                          {e.priority && <PriorityFlag />}
                          {waitedMinutes(e) != null && (
                            <span className="text-xs text-slate-400">waiting {waitedMinutes(e)}m</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => run("call", async () => {
                          const res = await queueAction(e.appointment_id, "call");
                          if (res.ok) toast.success(`Called ${e.patient_name}`);
                          return res;
                        })}
                        disabled={!!busy}
                        data-testid={`doctor-call-${e.appointment_id}`}
                        title="Call this patient"
                      >
                        <PhoneCall className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => run("skip", () => queueAction(e.appointment_id, "skip"))}
                        disabled={!!busy}
                        data-testid={`doctor-skip-${e.appointment_id}`}
                        title="Skip"
                      >
                        <SkipForward className="w-4 h-4 text-slate-400 hover:text-amber-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Skipped — recallable */}
          {skipped.length > 0 && (
            <Card className="border-amber-200" data-testid="doctor-skipped-list">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <SkipForward className="w-4 h-4 text-amber-600" /> Skipped ({skipped.length})
                </h3>
                <div className="space-y-2">
                  {skipped.map((e) => (
                    <div
                      key={e.appointment_id}
                      className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-amber-50/50"
                      data-testid={`doctor-skipped-row-${e.appointment_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.patient_name}</div>
                        <div className="text-xs text-slate-400">skipped {fmtTime(e.skipped_at)}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => run("recall", async () => {
                          const res = await queueAction(e.appointment_id, "recall");
                          if (res.ok) toast.success(`Recalled ${e.patient_name}`);
                          return res;
                        })}
                        disabled={!!busy}
                        data-testid={`doctor-recall-${e.appointment_id}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Recall
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completed today */}
          <Card className="border-slate-200" data-testid="doctor-completed-list">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Completed today ({completed.length})
              </h3>
              {completed.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400 italic">No consultations finished yet</div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-auto">
                  {completed.map((e) => (
                    <button
                      key={e.appointment_id}
                      type="button"
                      onClick={() => navigate(`/patients/${e.patient_id}`)}
                      className="w-full flex items-center justify-between gap-3 p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      data-testid={`doctor-completed-row-${e.appointment_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.patient_name}</div>
                        <div className="text-xs text-slate-400">{fmtTime(e.completed_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <TokenBadge entry={e} />
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <PrescriptionDialog
        open={!!rxFor}
        onOpenChange={(v) => !v && setRxFor(null)}
        patientId={rxFor?.patient_id}
        doctorName={doctorName}
        onSaved={() => {
          setRxFor(null);
          onChanged && onChanged();
        }}
      />
    </div>
  );
}
